import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';

import FullCalendar, {
    CustomContentGenerator,
    EventContentArg,
    EventInput,
    EventSourceInput,
    ViewContentArg
} from '@fullcalendar/react'; // must go before plugins
import dayGridPlugin from '@fullcalendar/daygrid'; // a plugin!
import timeGridPlugin from '@fullcalendar/timegrid'; // a plugin!
import listPlugin from '@fullcalendar/list'; // a plugin!
import momentPlugin from '@fullcalendar/moment'; // a plugin!
import Select from 'react-select';
import createStore from './store';
import { calendarApi } from '../../features/calendar';
import { DateUtility, DayJSDateStringFormat } from '../../utilities/date';
import { IEventInformation } from '../../models/event';
import ListMonthView from '../../calendar-views/list-month';
import { ApiUtility } from '../../utilities/api';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { ErrorBoundary } from '../../components/error-boundary';
import { Spinner } from '../../components/spinner';
import { Alert, AlertType } from '../../components/alert';
import { ISelectOption, UskinnedSelectStyles } from '../../models/select';
import MonthView from '../../calendar-views/month';
import { object, string } from 'prop-types';
import { ICalendarFilter } from '../../models/calendar-filter';
import moment = require('moment');

const enum CalendarViewType {
    DayGridMonth = 'dayGridMonth',
    TimeGridWeek = 'timeGridWeek',
    TimeGridDay = 'timeGridDay',
    ListMonth = 'listMonth'
}

// Custom event content renderer for each calendar view
// NOTE: This will only replace the view of the event content
// Other parts of the calendar UI will still remain
const EventContentRenderer: CustomContentGenerator<EventContentArg> = eventInfo => {
    if (eventInfo.view.type === CalendarViewType.ListMonth) {
        return <ListMonthView {...eventInfo} />;
    } else if (eventInfo.view.type === CalendarViewType.DayGridMonth) {
        return <MonthView {...eventInfo} />;
    }

    // return nothing to keep the default content view
};

export const CalendarApp = () => {
    const calendarRef = React.useRef<FullCalendar>();

    // Handle getting categories - START
    const {
        data: filterOptions,
        isLoading: isLoadingCategory,
        error: categoryError,
        isError: isCategoryError
    } = calendarApi.useGetCategoriesQuery();

    const categoryErrorMessage = React.useMemo(() => {
        return ApiUtility.getFetchErrorMessage(categoryError as FetchBaseQueryError);
    }, [categoryError]);

    const { categoryOptions, locationOptions } = React.useMemo(() => {

        const categoryOptions = [];
        const locationOptions = [];

        if (!filterOptions) {
            return {
                categoryOptions,
                locationOptions
            };
        }

        if (filterOptions.eventCategories && filterOptions.eventCategories.length) {
            filterOptions.eventCategories.forEach((categoryName) => {
                categoryOptions.push({
                    value: categoryName,
                    label: categoryName
                });
            });
        }

        if (filterOptions.locations && filterOptions.locations.length) {
            filterOptions.locations.forEach((venue) => {
                locationOptions.push({
                    value: venue.id,
                    label: venue.name

                });
            });
        }

        return {
            categoryOptions,
            locationOptions
        };
    }, [filterOptions]);
    // Handle getting categories - END



    // Handle getting calendar events - START
    // getEvents API
    const [getEvents, { data: eventsData, isError: isGetEventError, error: getEventError, isLoading: isQueringEvents }] =
        calendarApi.useGetEventsMutation();

    const getEventErrorMessage = React.useMemo(() => {
        return ApiUtility.getFetchErrorMessage(getEventError as FetchBaseQueryError);
    }, [getEventError]);

    // filter value (by categories)
    const [categoryFilter, setCategoryFilter] = React.useState(null);

    // filter value (by location)
    const [locationFilter, setLocationFilter] = React.useState(null);

    // timeframe (start and end date) for the list of events (controlled by the calendar)
    const [timeframe, setTimeframe] = React.useState(null);

    // calling getEvents API based on the timeframe's and filter's values
    React.useEffect(() => {
        if (!timeframe) {
            return;
        }

        let filterParams: ICalendarFilter = {
            startDateInput: timeframe.start,
            endDateInput: timeframe.end,
            category: "",
            location: ""
        };

        if (categoryFilter && categoryFilter.length) {
            filterParams.category = categoryFilter;
        }

        if (locationFilter && locationFilter.length) {
            filterParams.location = locationFilter;
        }

        getEvents(filterParams);
    }, [timeframe?.start, timeframe?.end, categoryFilter, locationFilter]);

    // event source object used by full calendar
    // this includes event data transform function to convert API event object to Full Calendar's event model
    const eventSource: EventSourceInput = React.useMemo(
        () => ({
            events: function (info, successCallback, failureCallback) {
                const start = DateUtility.toFormattedString(info.start, DayJSDateStringFormat.YYYYMMDD);
                const end = DateUtility.toFormattedString(info.end, DayJSDateStringFormat.YYYYMMDD);
                if (!timeframe || timeframe.start !== start || timeframe.end !== end) {
                    setTimeframe({
                        start,
                        end
                    });
                }
                successCallback(eventsData || []);
            },
            eventDataTransform: function (eventData: Omit<IEventInformation, 'id'>) {
                const standardEventObject: EventInput = { extendedProps: {} };

                // setup event start, end
                const {
                    category,
                    formattedDates,
                    instancePurchaseUrl,
                    websiteDetailLink,
                    eventPurchaseUrl,
                    name
                } = eventData || {};
                if (!formattedDates) {
                    return null;
                }

                let eventDate = new Date(formattedDates);
                let time = moment(formattedDates).format("h:mm a");

                standardEventObject.start = eventDate
                standardEventObject.extendedProps.time = time;

                // setup event title and url
                standardEventObject.title = name;
                standardEventObject.url = websiteDetailLink == null ? instancePurchaseUrl : websiteDetailLink;
                standardEventObject.textColor = '#4D595C';
                standardEventObject.backgroundColor = "#CC00FF"

                // We have to set explicitly the block view
                // because in 'month' view some events where not
                // being painted with the corresponding color,
                // the reazon is not clear, but it seems is
                // it happens when the end time is not defined for
                // the event
                standardEventObject.display = 'block';

                // setup event details
                standardEventObject.extendedProps.categories = category;
                standardEventObject.extendedProps.moreInfo = instancePurchaseUrl;
                standardEventObject.extendedProps.eventTitle = name;

                return standardEventObject;
            }
        }),
        [eventsData]
    );

    const handleSize = (event: ViewContentArg): void => {
        let calendar = event.view.calendar;
        if (window.innerWidth < 992) {
            calendar.changeView(CalendarViewType.ListMonth);
        } else {
            calendar.changeView(CalendarViewType.DayGridMonth);
        }
    }

    // START RENDERING VIEWS
    // Case loading
    if (isLoadingCategory) {
        return (
            <div className='form-state-block d-flex align-items-center justify-content-center'>
                <Spinner />
            </div>
        );
    } else if (isCategoryError) {
        // Case error with getting categories
        return (
            <Alert type={AlertType.Error}>
                <div>Something went wrong while getting the list of categories!</div>
                {categoryErrorMessage}
            </Alert>
        );
    }

    var currentDate = new Date();
    var startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

    // Temporal date with the date one year from the current date and time
    const aYearFromNow = new Date(currentDate);
    aYearFromNow.setMonth(aYearFromNow.getMonth() + 15);

    // To calcualte the end date, first we move to the day 1 at 00:00 a.m. of the next month
    // second, we remove one minute, that way we are sure the date is the last day of the previous month at 23:59.
    // we calculate it this way to cover the case of the months with 28, 30 and 31 days. We delegate
    // the calculation of how many days a month has to the Date object
    const nextMonth = aYearFromNow.getMonth() + 1;
    const endDate = new Date(aYearFromNow.getFullYear(), nextMonth, 1, 0, 0, 0, 0);
    endDate.setMinutes(endDate.getMinutes() - 1);

    // Case showing calendar
    return (
        <>
            {isGetEventError ? (
                <Alert type={AlertType.Error}>
                    <div>Something went wrong while getting the list of events!</div>
                    {getEventErrorMessage}
                </Alert>
            ) : (
                ''
            )}
            {/* Row 1 - Month Picker & Category filter */}

            {isQueringEvents ? (<Spinner isOverlay={true} />) : ('')}

            <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, listPlugin, momentPlugin]}

                initialView={CalendarViewType.DayGridMonth}
                validRange={{
                    start: startDate,
                    end: endDate
                }}
                headerToolbar={{
                    left: 'prev title next',
                    center: '',
                    right: [
                        CalendarViewType.DayGridMonth,
                        CalendarViewType.ListMonth
                    ].join(',') // no spacing after comma is necessary
                }}

                eventSources={[eventSource]}
                eventContent={EventContentRenderer}
                contentHeight='auto'
                eventClick={event => {
                    // NOTE: can possibly replace this by having custom link in custom view
                    // for month/week/day view

                    // For the view List Month we have a custom html with More Info Link
                    // in that case we have to check where the click was made,
                    // to do the propper redirect.
                    //
                    // If the clicked html component is a link we go to its href.
                    // otherwise we continue the normal flow to open the event detail page
                    if (event.view.type == CalendarViewType.ListMonth) {
                        var element = event.jsEvent.target as any;
                        if (element.className === "fc-list-event-time") {
                            event.jsEvent.preventDefault();
                            window.open(event.event.extendedProps.moreInfo, '_blank');
                        }
                    }

                    // For any other view different to the List Month
                    // we open the event link
                    if (event.event.url) {
                        var element = event.jsEvent.target as any;
                        if (element.parentNode.tagName === 'A') {
                            event.jsEvent.preventDefault();
                            window.open(element.parentNode.href, '_blank');
                        }
                    }
                }}
                listDayFormat='dddd, MMMM Do'
                listDaySideFormat={false}
                lazyFetching={false}
                windowResize={handleSize}
                dayHeaderDidMount={() => {
                    // replace <a> tag from month day heads
                    const monthDayEls = document.querySelectorAll('.fc-col-header-cell > .fc-scrollgrid-sync-inner');
                    monthDayEls.forEach((element) => {
                        element.innerHTML = element.innerHTML.replace('<a', '<span');
                    });

                    // replace <a> tag from list day heads
                    const listDayEls = document.querySelectorAll('.fc-list-day > th > .fc-list-day-cushion');
                    listDayEls.forEach((element) => {
                        element.innerHTML = element.innerHTML.replace('<a', '<span');
                    });
                }}
                eventsSet={() => {
                    // add presentation role to calendar table
                    const listTable = document.querySelector('.fc-list-table');
                    if (listTable) {
                        listTable.setAttribute('role', 'presentation');
                    }

                    // add group role to list 
                    const listTableBody = document.querySelector('.fc-list-table tbody');
                    if (listTableBody) {
                        listTableBody.setAttribute('role', 'group');
                    }

                    // replace <a> tag from month numbers
                    const monthNumbers = document.querySelectorAll('.fc-daygrid-day-top');
                    monthNumbers.forEach((number) => {
                        number.innerHTML = number.innerHTML.replace('<a', '<span');
                    });

                    // add role/aria-label to cal list events
                    const listEvents = document.querySelectorAll('.fc-list-event');
                    listEvents.forEach((event) => {
                        event.setAttribute('role', 'group');
                        event.setAttribute('aria-label', 'event');
                    });

                    // update month view events
                    const events = document.querySelectorAll('.fc-daygrid-event-harness');
                    events.forEach((event) => {

                        const ticketLink = event.querySelector('.short-text-box a').getAttribute('href');

                        const pdpLink = event.querySelector('.fc-daygrid-event-harness a').getAttribute('href');

                        const calDay = event.parentElement.parentElement;
                        const eventDate = calDay.querySelector('.fc-daygrid-day-number').getAttribute('aria-label');

                        const eventTitle = event.querySelector('.short-event-text p').innerHTML;
                        const eventCategory = event.querySelector('.short-event-text label').innerHTML;
                        const eventTime = event.querySelector('.short-text-box span').innerHTML;

                        event.innerHTML = event.innerHTML.replace('<a', '<span');

                        event.querySelector('.short-event-text').innerHTML =
                            '<div>' +
                            '<p><a href="' + pdpLink + '" aria-label="Find out more about ' + eventTitle + '">' + eventTitle + '</a></p>' +
                            '<p>' + eventCategory + '</p>' +
                            '<p>' + eventTime + '</p>' +
                            '</div>' +
                            '<div class="short-text-box">' +
                            '<a href="' + ticketLink + '" aria-label="Buy tickets for ' + eventTitle + ' on ' + eventDate + ', at ' + eventTime + '"><span>Buy Tickets</span></a>' +
                            '</div>';
                    });
                }}
                viewDidMount={() => {
                    const prevBtnElement = document.querySelector(".fc-prev-button");
                    const nextBtnElement = document.querySelector(".fc-next-button");
                    prevBtnElement.setAttribute("aria-label", "Previous month");
                    prevBtnElement.removeAttribute("aria-pressed");
                    nextBtnElement.setAttribute("aria-label", "Next month");
                    nextBtnElement.removeAttribute("aria-pressed");
                }}
            />

        </>
    );
};

export class Calendar {
    constructor(element: HTMLElement) {
        const store = createStore();

        ReactDOM.render(
            <Provider store={store}>
                <ErrorBoundary>
                    <CalendarApp />
                </ErrorBoundary>
            </Provider>,
            element
        );
    }
}
